import { useMemo, useState } from "react";
import Skeleton from "@mui/material/Skeleton";
import styled from "styled-components";

import theme from "../../../styles/theme";
import { getFlag } from "../../../utils/helpers/flags";
import msToTime from "../../../utils/helpers/msToTime";
import formatDate from "../../../utils/helpers/formatDate";
import { NoDataMessage } from "../../../components/Common/TableStyles";
import { LoadFailure } from "./LoadFailure";
import { SESSION_LABELS } from "./sessions";
import { TrackModal } from "./TrackModal";
import plural from "../../../utils/helpers/plural";
import {
  Divider,
  Ellipsis,
  FilterInput,
  Grid,
  Pill,
  SectionHeader,
  SectionTitle,
  skeletonSx,
  skeletonWidth,
} from "../styles";
import { tyreImages } from "./tyreImages";

const SESSIONS = ["RACE", "SPRINT", "QUALIFICATION"];
const DEFAULT_SESSION = "RACE";

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const SessionGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Hint = styled.div`
  font-size: 11px;
  color: ${theme.colors.grey};
  margin: -8px 0 16px;
`;

const Tile = styled.div`
  background: ${theme.colors.darkLight};
  border: 2px solid ${theme.colors.secondaryDark};
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${theme.colors.green};
  }
`;

const ImageArea = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: ${theme.colors.dark};
  overflow: hidden;
`;

const TrackImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const FlagIcon = styled.img`
  width: 28px;
  height: auto;
  opacity: 0.8;
`;

const OverlayScrim = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 56px;
  background: linear-gradient(to bottom, ${theme.colors.dark}cc, transparent);
  pointer-events: none;
`;

const OverlayChips = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  display: flex;
  gap: 6px;
  pointer-events: none;
`;

const CountryChip = styled.span`
  font-family: "Roboto Condensed", sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  background: ${theme.colors.dark}e6;
  color: ${theme.colors.green};
  padding: 3px 7px;
`;

const SessionChip = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  background: ${theme.colors.dark}e6;
  color: ${theme.colors.white};
  padding: 4px 7px;
`;

const Body = styled.div`
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const NameBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TrackName = styled.span`
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
`;

const ConfigName = styled.span`
  font-size: 11px;
  color: ${theme.colors.grey};
`;

const TimeRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
`;

const Time = styled.span`
  font-family: "Roboto Condensed", sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: ${theme.colors.green};
  line-height: 1;
  letter-spacing: -0.5px;
`;

const TyreIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-bottom: 3px;
`;

const HolderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  &:hover {
    color: ${theme.colors.green};
  }
`;

const HolderName = styled(Ellipsis)`
  font-size: 13px;
  font-weight: 700;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Tag = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  background: ${theme.colors.secondaryDark};
  color: ${({ $muted }) => ($muted ? theme.colors.grey : theme.colors.white)};
  padding: 4px 7px;
`;

const RecordDate = styled.div`
  font-size: 11px;
  color: ${theme.colors.grey};
`;

const Footer = styled.div`
  margin-top: auto;
  border-top: 2px solid ${theme.colors.secondaryDark};
  padding-top: 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${theme.colors.grey};
`;

function configurationCount(track) {
  return new Set((track.configurations || []).map((c) => c.configuration)).size;
}

// The tile shows the track's default configuration — the layout the league actually races — so a
// shorter, quicker layout can no longer win the headline time. Falls back to the outright fastest
// only when the default has nothing under the current session/car filters, which keeps every track
// on the grid.
function tileRecord(track, session, car) {
  const candidates = (track.configurations || []).filter(
    (entry) => entry.sessionType === session && (car === "auto" || entry.car === car),
  );
  if (candidates.length === 0) return null;
  const preferred = candidates.filter((entry) => entry.defaultConfiguration);
  const pool = preferred.length > 0 ? preferred : candidates;
  return pool.reduce((best, entry) => (entry.time < best.time ? entry : best));
}

// Reuses the real Tile/ImageArea/Body chrome so the loading grid has the same tile footprint as
// the loaded one. Six tiles is roughly a first screenful at the 316px grid minimum.
const SKELETON_TILES = 6;

function TracksTabSkeleton() {
  return (
    <Grid $min="316px" $gap="12px">
      {Array.from({ length: SKELETON_TILES }, (_, index) => (
        <Tile key={index} style={{ cursor: "default" }}>
          <ImageArea>
            <Skeleton variant="rectangular" width="100%" height="100%" sx={skeletonSx} />
          </ImageArea>
          <Body>
            <NameBlock>
              <Skeleton variant="text" width={skeletonWidth(index)} height={20} sx={skeletonSx} />
              <Skeleton variant="text" width={skeletonWidth(index + 3)} height={14} sx={skeletonSx} />
            </NameBlock>
            <TimeRow>
              <Skeleton variant="text" width={128} height={38} sx={skeletonSx} />
            </TimeRow>
            <Skeleton variant="text" width={skeletonWidth(index + 6)} height={17} sx={skeletonSx} />
            <TagRow>
              <Skeleton variant="rectangular" width={72} height={22} sx={skeletonSx} />
              <Skeleton variant="rectangular" width={56} height={22} sx={skeletonSx} />
            </TagRow>
            <Skeleton variant="text" width={skeletonWidth(index + 9)} height={14} sx={skeletonSx} />
            <Footer>
              <Skeleton variant="text" width={104} height={13} sx={skeletonSx} />
            </Footer>
          </Body>
        </Tile>
      ))}
    </Grid>
  );
}

export function TracksTab({
  tracks,
  failed,
  onRetry,
  simulatorName,
  onSelectDriver,
  car,
  openTrackId,
  onOpenTrack,
  onCloseTrack,
}) {
  const [session, setSession] = useState(DEFAULT_SESSION);
  const [query, setQuery] = useState("");

  const tiles = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (tracks || [])
      .filter((t) => !q || t.name?.toLowerCase().includes(q) || t.shortName?.toLowerCase().includes(q))
      .map((t) => ({ track: t, best: tileRecord(t, session, car) }))
      .filter((entry) => entry.best)
      .map((entry) => ({ ...entry, recordDate: formatDate(entry.best.raceDate) }));
  }, [tracks, session, car, query]);

  const openTrack = openTrackId ? (tracks || []).find((t) => t.trackId === openTrackId) : null;

  return (
    <div>
      <SectionHeader $first>
        <SectionTitle>Рекорди треків</SectionTitle>
        <Toolbar>
          <SessionGroup role="radiogroup" aria-label="Тип сесії">
            {SESSIONS.map((s) => (
              <Pill
                key={s}
                role="radio"
                aria-checked={session === s}
                $active={session === s}
                onClick={() => setSession(s)}
              >
                {SESSION_LABELS[s] || s}
              </Pill>
            ))}
          </SessionGroup>
          <Divider />
          <FilterInput placeholder="Пошук треку…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </Toolbar>
      </SectionHeader>

      <Hint>
        Рекорди кожного треку в {simulatorName}. Відкрийте трек, щоб
        побачити всі його конфігурації.
      </Hint>

      {failed && <LoadFailure onRetry={onRetry} />}

      {!failed && !tracks && <TracksTabSkeleton />}

      {!failed && tracks && tiles.length === 0 && (
        <NoDataMessage>Немає рекордів за цими фільтрами</NoDataMessage>
      )}

      {!failed && tracks && tiles.length > 0 && (
        <Grid $min="316px" $gap="12px">
          {tiles.map(({ track, best, recordDate }) => (
            <Tile key={track.trackId} onClick={() => onOpenTrack(track.trackId)}>
              <ImageArea>
                {best.image || track.image ? (
                  <TrackImage
                    src={best.image || track.image}
                    alt={`${track.name} — ${best.configuration}`}
                  />
                ) : (
                  <ImagePlaceholder>
                    {getFlag(track.flag) && <FlagIcon src={getFlag(track.flag)} alt={track.country} />}
                  </ImagePlaceholder>
                )}
                <OverlayScrim />
                <OverlayChips>
                  {track.country && <CountryChip>{track.country}</CountryChip>}
                  <SessionChip>{SESSION_LABELS[best.sessionType] || best.sessionType}</SessionChip>
                </OverlayChips>
              </ImageArea>
              <Body>
                <NameBlock>
                  <TrackName>{track.name}</TrackName>
                  <ConfigName>{best.configuration}</ConfigName>
                </NameBlock>
                <TimeRow>
                  <Time>{msToTime(best.time)}</Time>
                  {tyreImages[best.tyreCompound] && (
                    <TyreIcon src={tyreImages[best.tyreCompound]} alt={best.tyreCompound} />
                  )}
                </TimeRow>
                <HolderRow
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectDriver(best.holderId);
                  }}
                >
                  <HolderName>{best.holderName}</HolderName>
                </HolderRow>
                <TagRow>
                  <Tag>{best.car}</Tag>
                  <Tag $muted>{simulatorName}</Tag>
                </TagRow>
                {recordDate && <RecordDate>{recordDate}</RecordDate>}
                <Footer>
                  {configurationCount(track) > 1
                    ? `${configurationCount(track)} ${plural(configurationCount(track), [
                        "конфігурація",
                        "конфігурації",
                        "конфігурацій",
                      ])} →`
                    : "деталі треку →"}
                </Footer>
              </Body>
            </Tile>
          ))}
        </Grid>
      )}

      <TrackModal
        track={openTrack}
        initialConfiguration={openTrack ? tileRecord(openTrack, session, car)?.configuration : null}
        onClose={onCloseTrack}
        onSelectDriver={onSelectDriver}
      />
    </div>
  );
}
